import { useParams } from 'react-router';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { Fragment } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import React from 'react';
import { DatabaseScheduleForRead } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseSchedulesForRead } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { getDateTimeAsReadableString } from 'libs/end-to-end/src/dates';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';

export const DatabaseSchedulesPage = () => {
  const language = useLanguage();
  const { organizationId, databaseId } = useParams();

  const readSchedulesPattern = useSimpleReadPattern(
    useMemo(() => {
      return {
        getUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedules`,
        canRead: true,
        responseSchema: DatabaseSchedulesForRead,
      };
    }, [databaseId, organizationId]),
  );

  const createNewSchedulePattern = useSimpleWritePattern(
    useMemo(() => {
      return {
        getUrlFn: () =>
          `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule`,
        writeSchema: EmptyObject,
        responseSchema: DatabaseScheduleForRead,
        canWrite: true,
        method: 'POST',
      };
    }, [databaseId, organizationId]),
  );

  const [deleteScheduleIdSignal, setDeleteScheduleIdSignal] = useState<
    string | null
  >(null);

  const [deleteSchedulePrompt, setDeleteSchedulePrompt] = useState<
    string | null
  >(null);

  const deleteSchedulePatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${deleteScheduleIdSignal}`;
  }, [organizationId, databaseId, deleteScheduleIdSignal]);

  const deleteSchedulePattern = useSimpleWritePattern(
    useMemo(() => {
      return {
        getUrlFn: deleteSchedulePatternGetUrlFn,
        writeSchema: EmptyObject,
        responseSchema: EmptyObject,
        canWrite: true,
        method: 'DELETE',
      };
    }, [deleteSchedulePatternGetUrlFn]),
  );

  const itemsToShow = useMemo(() => {
    if (readSchedulesPattern.status === 'done') {
      return readSchedulesPattern.entity.schedules;
    } else {
      return [];
    }
  }, [readSchedulesPattern]);

  const isLoading = useMemo(() => {
    return readSchedulesPattern.status === 'loading';
  }, [readSchedulesPattern]);

  const deleteSchedule = useCallback(() => {
    if (deleteSchedulePattern.status !== 'saving') {
      deleteSchedulePattern.saveEntity({}, () => {
        setDeleteScheduleIdSignal(null);
        setDeleteSchedulePrompt(null);
        if (readSchedulesPattern.status !== 'loading') {
          readSchedulesPattern.reloadEntity();
        }
      });
    }
  }, [deleteSchedulePattern, setDeleteScheduleIdSignal, readSchedulesPattern]);

  useEffect(() => {
    if (deleteScheduleIdSignal !== null) {
      deleteSchedule();
    }
  }, [deleteScheduleIdSignal, deleteSchedule]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteSchedulePrompt) {
      setDeleteScheduleIdSignal(deleteSchedulePrompt);
    }
  }, [deleteSchedulePrompt]);

  return (
    <AdminLayout currentPage={'DatabaseSchedules'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Schedules',
            to: `/organizations/${organizationId}/databases/${databaseId}/schedules`,
          },
        ]}
      />
      <Spacing size={'lg'} direction={'vertical'}>
        <S2Typography
          variant={'headline-4'}
          translatedContent={language.dangerousUserInput({
            value: 'Database Schedules',
          })}
        />

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (createNewSchedulePattern.status !== 'saving') {
                createNewSchedulePattern.saveEntity({}, () => {
                  if (readSchedulesPattern.status !== 'loading') {
                    readSchedulesPattern.reloadEntity();
                  }
                });
              }
            },
          }}
          disabled={
            createNewSchedulePattern.status === 'saving' ||
            deleteSchedulePattern.status === 'saving' ||
            readSchedulesPattern.status === 'loading'
          }
        >
          Create new
        </Button>

        <Table
          columnDefinitions={[
            {
              id: 'name',
              header: () => 'Name',
              cell: ({ item }) => {
                return <>{item.name}</>;
              },
            },
            {
              id: 'id',
              header: () => 'ID',
              cell: ({ item }) => {
                return <>{item.scheduleId}</>;
              },
            },
            {
              id: 'status',
              header: () => 'Status',
              cell: ({ item }) => {
                return <>{item.status}</>;
              },
            },
            {
              id: 'schedule',
              header: () => 'Schedule',
              cell: ({ item }) => {
                return <>{item.scheduleConfigurationType}</>;
              },
            },
            {
              id: 'nextScheduledRun',
              header: () => 'Next Run',
              cell: ({ item }) => {
                return (
                  <>
                    {item.details.nextScheduledEpoch
                      ? getDateTimeAsReadableString(
                          new Date(item.details.nextScheduledEpoch),
                        )
                      : 'N/A'}
                  </>
                );
              },
            },
            {
              id: 'edit',
              header: () => 'Edit',
              cell: ({ item }) => {
                return (
                  <Button
                    behavior={{
                      type: 'href',
                      href: `/organizations/${organizationId}/databases/${databaseId}/schedule/${item.scheduleId}`,
                    }}
                    variant={'outline'}
                    size={'small'}
                  >
                    Edit
                  </Button>
                );
              },
            },
            {
              id: 'delete',
              header: () => 'Delete',
              cell: ({ item }) => {
                return (
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        setDeleteSchedulePrompt(item.scheduleId);
                      },
                    }}
                    variant={'outline'}
                    size={'small'}
                    disabled={
                      deleteSchedulePattern.status === 'saving' ||
                      readSchedulesPattern.status === 'loading' ||
                      createNewSchedulePattern.status === 'saving'
                    }
                  >
                    Delete
                  </Button>
                );
              },
            },
          ]}
          items={itemsToShow}
          isLoading={isLoading}
        />

        <Transition appear show={!!deleteSchedulePrompt} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-overlay"
            onClose={() => setDeleteSchedulePrompt(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Delete database schedule
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are about to delete a database schedule which could
                        break things. Are you sure you want to do this?
                      </p>
                    </div>

                    <div className="mt-1">
                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={handleConfirmDelete}
                        disabled={deleteSchedulePattern.status === 'saving'}
                        className="mt-2"
                      >
                        Delete
                      </LegacyButton>

                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={() => setDeleteSchedulePrompt(null)}
                        disabled={deleteSchedulePattern.status === 'saving'}
                        className="mt-4 ml-4"
                      >
                        Cancel
                      </LegacyButton>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </Spacing>
    </AdminLayout>
  );
};
